import React, { useState, useEffect, useRef } from 'react';
import { Link, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { initKeycloak, getKeycloakInstance, getCurrentRealm } from "../keycloak";
import { logout } from "../keycloak";
import RuleListing from './RuleListing';
import RuleSetListing from './RuleSetListing';
import ApprovalRulesets from './ApprovalRulesets';
import ExecutionResultsUI from './ExecutionResultsUI';
import SandboxUI from './SandboxUI';
import AnalyticsDashboard from './AnalyticsDashboard';
import RuleEditor from "./RuleEditor";
import RulesView from "./RulesView";
import ManageRuleSets from "./ManageRuleSets";
import ReportingDashboardHome from "./ReportingDashboardHome";
import SSBCounterpartyConcentrationReport from "./SSBCounterpartyConcentrationReport";
import SSBProgramConcentrationReport from "./SSBProgramConcentrationReport";
import SSBTicketSizeDistributionReport from "./SSBTicketSizeDistributionReport";
import SSBCountryExposureReport from "./SSBCountryExposureReport";
import SSBPortfolioRiskHeatmap from "./SSBPortfolioRiskHeatmap";
import SSBAmlExecutionReport from "./SSBAmlExecutionReport";
import SSBHighRiskAlertsReport from "./SSBHighRiskAlertsReport";
import SSBComplianceRemediationAgent from "./SSBComplianceRemediationAgent";
import {
  isSSBAMLEnabled,
  isNewDashboard,
  isPortfolioDashboardV2,
  isSidebarV2,
  showCaseManagementLink,
  enableCaseManagementLink,
  enableStablecoinDashboard,
  isSidebarBlue,
  isIsoDashboard,
} from "../featureFlags";
import DepositOutcomeDashboardPro from "./DepositOutcomeDashboardPro";
import DepositOutcomeDashboardProV2 from "./DepositOutcomeDashboardProV2";
import StablecoinRiskConsole from "./StablecoinRiskConsole";
import ApprovalUpliftConsole from "./ApprovalUpliftConsole";
import NavigatorLaunch from "./NavigatorLaunch";
import ISOPortfolioDashboard from "./ISOPortfolioDashboard";
import { Button } from 'react-bootstrap';
import {
  FaUserCircle,
  FaFileAlt,
  FaFolderOpen,
  FaVial,
  FaChevronLeft,
  FaChevronRight,
  FaFlask,
  FaList,
  FaBriefcase,
} from 'react-icons/fa';
import { MdOutlineDashboard } from 'react-icons/md';
import {
  FileText,
  Folders,
  ShieldCheck,
  FlaskConical,
  ListChecks,
  LayoutDashboard,
  FileBarChart,
  Briefcase,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  CircleUser,
  Search,
  ChevronDown,
  MoreHorizontal,
  ToggleRight,
} from 'lucide-react';
import { FeatureFlagsPanel } from "./FeatureFlagsPanel";

// ═══════════════════════════════════════════════════════════════════════════
// NavigatorWithRoutes — top-level router + sidebar chooser.
//   Sidebar V1 (isSidebarV2=false) = original pre-facelift cobalt sidebar,
//     preserved verbatim from the previous production build.
//   Sidebar V2 (isSidebarV2=true)  = Idenza design-system sidebar.
// Routes are declared here (unchanged either way) so the two sidebar
// variants share the SAME route table — zero drift risk.
// ═══════════════════════════════════════════════════════════════════════════
const NavigatorWithRoutes = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [globalFlagsOpen, setGlobalFlagsOpen] = useState(false);

  // Global escape hatch: Cmd/Ctrl + Shift + F opens the Feature Flags
  // panel from any page, any sidebar. Prevents the chicken-and-egg where
  // turning SidebarV2 off hides the flags UI.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault();
        setGlobalFlagsOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);
  const navigate = useNavigate();
  const isInitialized = useRef(false);
  const [realmInfo, setRealmInfo] = useState({ username: "", tenantId: "" });

  // Default points at the case-management SPA served by the stage nginx
  // (see /app/nginx/conf.d/frontend.conf on the staging VM — routes
  // /casemanagement/* to the case-frontend container). Override via
  // REACT_APP_CASE_MGMT_URL for local dev or other envs.
  const CASE_MGMT_BASE =
    process.env.REACT_APP_CASE_MGMT_URL || "https://demo.idenza.ai/casemanagement";

  const withRealm = (path) => {
    const r = getCurrentRealm();
    return `${path}?realm=${encodeURIComponent(r)}`;
  };

  const isSSBReportingRealm = () => {
    const realm = (getCurrentRealm() || sessionStorage.getItem("active_realm") || "")
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    return ["ssb", "ssbbank"].includes(realm);
  };

  const showReportingDashboard = isSSBAMLEnabled && isSSBReportingRealm();

  // Senior's addition (07/28) — stablecoin dashboard is realm-gated:
  // only shows for Idenza + Thread bank tenants where the launch page
  // makes sense (both merchant-acquiring and stablecoin surfaces).
  const isStablecoinRealm = () => {
    const realm = (getCurrentRealm() || sessionStorage.getItem("active_realm") || "")
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    return ["idenza", "thread", "threadbank"].includes(realm);
  };
  const showStablecoinDashboard = enableStablecoinDashboard && isStablecoinRealm();

  // Handler for NavigatorLaunch quick-link tiles. External links open in
  // a new tab; internal links navigate through react-router.
  const handleLaunchQuickLink = (href, link) => {
    if (link?.external) {
      window.open(
        `${CASE_MGMT_BASE}/?realm=${encodeURIComponent(getCurrentRealm())}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    navigate(withRealm(href));
  };

  const location = useLocation();

  useEffect(() => {
    const url = new URL(window.location.href);
    const hasRealm = url.searchParams.has("realm");
    const storedRealm = sessionStorage.getItem("active_realm");
    if (!hasRealm && storedRealm) {
      url.searchParams.set("realm", storedRealm);
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const hasAuthParamsInUrl = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.has("code") || params.has("session_state");
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith("/login")) {
      setLoading(false);
      return;
    }

    if (isInitialized.current) {
      const kc = getKeycloakInstance();
      if (kc?.authenticated && kc.token) {
        const username =
          sessionStorage.getItem("username") ||
          kc.tokenParsed?.preferred_username ||
          "User";
        const tenantId =
          kc.tokenParsed?.tenant_id ||
          sessionStorage.getItem("tenant_id") ||
          "";
        setRealmInfo({ username, tenantId });
        setAuthenticated(true);
      } else {
        setAuthenticated(false);
      }
      setLoading(false);
      return;
    }

    let cancelled = false;
    const hadAuthParams = hasAuthParamsInUrl();

    const run = async () => {
      try {
        const keycloak = await initKeycloak();
        if (cancelled) return;

        if (keycloak?.authenticated && keycloak.token) {
          isInitialized.current = true;
          localStorage.setItem("access_token", keycloak.token);
          sessionStorage.setItem("access_token", keycloak.token);

          const username =
            sessionStorage.getItem("username") ||
            keycloak.tokenParsed?.preferred_username ||
            "User";
          const tenantId =
            keycloak.tokenParsed?.tenant_id ||
            sessionStorage.getItem("tenant_id") ||
            "";

          setRealmInfo({ username, tenantId });
          setAuthenticated(true);

          if (window.location.pathname === "/") {
            navigate("/rules", { replace: true });
          }
        } else {
          setAuthenticated(false);
          if (hadAuthParams) return;
          const redirectUri = window.location.href;
          await keycloak.login({ redirectUri });
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Navigator: initKeycloak error:", err);
          setAuthenticated(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [location.pathname, navigate]);

  const sharedNavProps = {
    withRealm,
    realmInfo,
    showReportingDashboard,
    showStablecoinDashboard,
    showIsoDashboard: isIsoDashboard,
    CASE_MGMT_BASE,
  };

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        fontFamily: isSidebarV2
          ? "'Inter Variable', 'Inter', system-ui, -apple-system, sans-serif"
          : 'Arial, sans-serif',
        color: isSidebarV2 ? 'var(--text-primary)' : undefined,
      }}
    >
      {isSidebarV2
        ? <SidebarV2 {...sharedNavProps} />
        : <SidebarV1 {...sharedNavProps} />
      }

      {/* Main Content — routes shared across both sidebars. */}
      <div
        style={{
          flexGrow: 1,
          padding: isSidebarV2 ? 0 : '20px',
          background: isSidebarV2 ? 'var(--bg-app)' : '#F4F6F9',
          minWidth: 0,
        }}
      >
        <Routes>
          <Route path="/*" element={<Navigate to="/rules" />} />
          <Route path="/rules" element={<RuleListing />} />
          <Route path="/approve-rulesets" element={<ApprovalRulesets />} />
          <Route path="/rulesets" element={<RuleSetListing />} />
          <Route path="/sandbox" element={<SandboxUI />} />
          <Route path="/execution-results" element={<ExecutionResultsUI />} />
          {/* Senior's launch page — routes to the two risk consoles
              (acquiring + stablecoin). Realm-gated: only shows when
              enableStablecoinDashboard AND the realm is one of the
              stablecoin-eligible tenants. Falls back to /dashboard for
              everyone else so the sidebar link never dead-ends. */}
          <Route
            path="/launch"
            element={
              showStablecoinDashboard ? (
                <NavigatorLaunch
                  user={realmInfo?.username}
                  routes={{
                    acquiring: withRealm("/dashboard"),
                    stablecoin: withRealm("/stablecoin"),
                    approval: withRealm("/approval"),
                    sar: `${CASE_MGMT_BASE}/?realm=${encodeURIComponent(getCurrentRealm())}`,
                  }}
                  onOpen={(key) => {
                    if (key === "sar") {
                      window.open(
                        `${CASE_MGMT_BASE}/?realm=${encodeURIComponent(getCurrentRealm())}`,
                        "_blank",
                        "noopener,noreferrer"
                      );
                      return;
                    }
                    navigate(
                      withRealm(
                        key === "stablecoin" ? "/stablecoin"
                        : key === "approval" ? "/approval"
                        : "/dashboard"
                      )
                    );
                  }}
                  onQuickLink={handleLaunchQuickLink}
                />
              ) : (
                <Navigate to={withRealm("/dashboard")} replace />
              )
            }
          />

          {/* Dashboard route — 3-way toggle:
              isNewDashboard=false → legacy AnalyticsDashboard
              isNewDashboard=true  + isPortfolioDashboardV2=false → pre-facelift Pro
              isNewDashboard=true  + isPortfolioDashboardV2=true  → facelifted Pro V2
              When stablecoin realm is active, prepend a BackToLaunch
              link so users can hop back to the launch page. */}
          <Route
            path="/dashboard"
            element={
              <>
                {showStablecoinDashboard && (
                  <BackToLaunch navigate={navigate} withRealm={withRealm} />
                )}
                {
                  !isNewDashboard ? <AnalyticsDashboard />
                  : isPortfolioDashboardV2 ? <DepositOutcomeDashboardProV2 />
                  : <DepositOutcomeDashboardPro />
                }
              </>
            }
          />

          {showStablecoinDashboard && (
            <Route
              path="/stablecoin"
              element={
                <>
                  <BackToLaunch navigate={navigate} withRealm={withRealm} />
                  <StablecoinRiskConsole />
                </>
              }
            />
          )}

          {showStablecoinDashboard && (
            <Route
              path="/approval"
              element={
                <>
                  <BackToLaunch navigate={navigate} withRealm={withRealm} />
                  <ApprovalUpliftConsole showHeader={false} realm={getCurrentRealm()} />
                </>
              }
            />
          )}
          {isIsoDashboard && (
            <Route path="/iso-portfolio" element={<ISOPortfolioDashboard />} />
          )}
          <Route path="/edit-rule/:ruleId" element={<RuleEditor />} />
          <Route path="/view-rule/:ruleId" element={<RulesView />} />
          <Route path="/manage-rulesets/:rulesetId" element={<ManageRuleSets />} />
          {showReportingDashboard && (
            <>
              <Route path="/reporting" element={<ReportingDashboardHome />} />
              <Route path="/reporting/counterparty-concentration" element={<SSBCounterpartyConcentrationReport />} />
              <Route path="/reporting/program-concentration" element={<SSBProgramConcentrationReport />} />
              <Route path="/reporting/ticket-bands" element={<SSBTicketSizeDistributionReport />} />
              <Route path="/reporting/country-exposure" element={<SSBCountryExposureReport />} />
              <Route path="/reporting/portfolio-risk-heatmap" element={<SSBPortfolioRiskHeatmap />} />
              <Route path="/reporting/aml-execution" element={<SSBAmlExecutionReport />} />
              <Route path="/reporting/high-risk-alerts" element={<SSBHighRiskAlertsReport />} />
              <Route path="/reporting/compliance-remediation" element={<SSBComplianceRemediationAgent />} />
            </>
          )}
        </Routes>
      </div>

      {/* Global escape hatch for the Feature Flags panel — always
          available regardless of sidebar version. Discreet floating
          button bottom-right + keyboard shortcut (Cmd/Ctrl+Shift+F). */}
      <button
        type="button"
        onClick={() => setGlobalFlagsOpen(true)}
        title="Feature flags (⌘⇧F)"
        aria-label="Feature flags"
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          width: 32,
          height: 32,
          borderRadius: 8,
          border: '1px solid rgba(0,0,0,0.10)',
          background: '#FFFFFF',
          color: '#6B7280',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          boxShadow: '0 4px 12px -2px rgba(10,10,10,0.10)',
          zIndex: 1000,
          opacity: 0.7,
          transition: 'opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.7)}
      >
        <ToggleRight size={14} strokeWidth={1.75} />
      </button>

      {globalFlagsOpen && (
        <FeatureFlagsPanel onClose={() => setGlobalFlagsOpen(false)} />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SidebarV1 — original pre-facelift cobalt sidebar. Preserved verbatim from
// the prior production build so falling back to `isSidebarV2=false` restores
// exactly the previous experience.
// ═══════════════════════════════════════════════════════════════════════════
const SidebarV1 = ({ withRealm, realmInfo, showReportingDashboard, showStablecoinDashboard, showIsoDashboard, CASE_MGMT_BASE }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  return (
    <div style={{
      width: isExpanded ? '260px' : '80px',
      background: isSidebarBlue
        ? 'linear-gradient(180deg, #2F6FED 0%, #1D3F8F 100%)'
        : 'linear-gradient(180deg, #0e8f9e 0%, #0b6b76 100%)',
      color: '#FFF',
      transition: 'width 0.3s ease-in-out',
      padding: isExpanded ? '20px' : '10px',
      boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: isExpanded ? 'flex-start' : 'center',
      flexShrink: 0,
      minHeight: '100vh',
      position: 'sticky',
      top: 0,
      height: '100vh',
      boxSizing: 'border-box',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: isExpanded ? 'space-between' : 'center',
        width: '100%',
        paddingBottom: '10px',
        borderBottom: '1px solid #ffffff40',
        marginBottom: '20px',
      }}>
        {isExpanded ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, background: isSidebarBlue ? '#10214A' : '#063C43', color: isSidebarBlue ? '#FFFFFF' : '#E5F1F2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 18, fontWeight: 700,
              marginRight: 10, flex: '0 0 auto',
            }}>I</div>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>Navigator</h2>
          </div>
        ) : (
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: isSidebarBlue ? '#10214A' : '#063C43', color: isSidebarBlue ? '#FFFFFF' : '#E5F1F2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 18, fontWeight: 700,
          }}>I</div>
        )}
        <Button
          variant="light"
          size="sm"
          className="ms-auto"
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? "Collapse" : "Expand"}
          style={{
            borderRadius: '50%',
            padding: '6px 10px',
            backgroundColor: '#ffffff30',
            color: '#FFF',
            border: 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        >
          {isExpanded ? <FaChevronLeft /> : <FaChevronRight />}
        </Button>
      </div>

      <p style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', margin: '10px 0 5px 12px', opacity: 0.7 }}>OPERATIONS</p>
      <ul style={{ listStyleType: 'none', padding: 0, width: '100%' }}>
        <NavItemV1 to={withRealm("/rules")} icon={<FaFileAlt />} text="Rules" isExpanded={isExpanded} />
        <NavItemV1 to={withRealm("/rulesets")} icon={<FaFolderOpen />} text="Rulesets" isExpanded={isExpanded} />
        <NavItemV1 to={withRealm("/approve-rulesets")} icon={<FaVial />} text="Approve Rulesets" isExpanded={isExpanded} />
      </ul>

      <p style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', margin: '10px 0 5px 12px', opacity: 0.7 }}>SANDBOX & RESULTS</p>
      <ul style={{ listStyleType: 'none', padding: 0, width: '100%' }}>
        <NavItemV1 to={withRealm("/sandbox")} icon={<FaFlask />} text="Sandbox" isExpanded={isExpanded} />
        <NavItemV1 to={withRealm("/execution-results")} icon={<FaList />} text="Execution Results" isExpanded={isExpanded} />
      </ul>

      <p style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', margin: '10px 0 5px 12px', opacity: 0.7 }}>DASHBOARD</p>
      <ul style={{ listStyleType: 'none', padding: 0, width: '100%' }}>
        {/* Senior's flow (07/28) — stablecoin realm routes through the
            NavigatorLaunch page at /launch. Non-stablecoin realms go
            straight to /dashboard. Item highlights on any of /launch,
            /dashboard, /stablecoin. */}
        <NavItemV1
          to={withRealm(showStablecoinDashboard ? "/launch" : "/dashboard")}
          icon={<MdOutlineDashboard />}
          text={showStablecoinDashboard ? "Dashboards" : "Dashboard"}
          isExpanded={isExpanded}
          activeFor={["/launch", "/dashboard", "/stablecoin"]}
        />
        {showIsoDashboard && (
          <NavItemV1 to={withRealm("/iso-portfolio")} icon={<MdOutlineDashboard />} text="ISO Portfolio" isExpanded={isExpanded} isNew />
        )}
        {showReportingDashboard && (
          <NavItemV1 to={withRealm("/reporting")} icon={<MdOutlineDashboard />} text="Reporting Dashboard" isExpanded={isExpanded} isNew />
        )}
      </ul>

      <p style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', margin: '10px 0 5px 12px', opacity: 0.7 }}>EXTERNAL LINKS</p>
      <ul style={{ listStyleType: 'none', padding: 0, width: '100%' }}>
        {showCaseManagementLink && (
          <NavItemExternalV1
            url={`${CASE_MGMT_BASE}/?realm=${encodeURIComponent(getCurrentRealm())}`}
            icon={<FaBriefcase />}
            text="Case Management"
            isExpanded={isExpanded}
            disabled={!enableCaseManagementLink}
          />
        )}
      </ul>

      <div style={{
        padding: "10px 10px",
        backgroundColor: isSidebarBlue ? "#1D3F8F" : "#0b6b76",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "40px",
        marginTop: "auto",
        gap: "8px",
        width: '100%',
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <FaUserCircle style={{ fontSize: "20px", color: "#FFF", marginRight: "6px" }} />
          <span
            style={{
              fontSize: "12px",
              color: "#FFF",
              fontWeight: "bold",
              maxWidth: "120px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={realmInfo?.username || "User"}
          >
            {realmInfo?.username || "User"}
          </span>
        </div>
        <button
          onClick={() => logout()}
          style={{
            fontSize: "10px",
            padding: "4px 8px",
            borderRadius: "999px",
            border: "none",
            backgroundColor: "#ffffff30",
            color: "#FFF",
            cursor: "pointer",
          }}
          title="Logout / Switch Tenant"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

const NavItemV1 = ({ to, icon, text, isExpanded, isNew = false, activeFor }) => {
  const currentPath = window.location.pathname;
  // activeFor: string[] of path prefixes. Used so a single Dashboards
  // item can highlight for /launch, /dashboard, and /stablecoin.
  const isActive = activeFor
    ? activeFor.some((p) => currentPath.startsWith(p))
    : currentPath === (to.split("?")[0] || to);
  return (
    <li style={{ marginBottom: '10px', width: '100%' }}>
      <Link
        to={to}
        title={!isExpanded ? text : ''}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px',
          borderRadius: '10px',
          textDecoration: 'none',
          color: '#FFF',
          fontWeight: isActive ? '600' : '500',
          background: isActive ? 'linear-gradient(to right, #ffffff26, #ffffff13)' : 'transparent',
          borderLeft: isActive ? '4px solid #ffffff' : '4px solid transparent',
          transition: 'all 0.2s ease-in-out',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = '#ffffff20';
          e.currentTarget.style.transform = 'scale(1.01)';
          e.currentTarget.style.boxShadow = '0 0 8px rgba(0,0,0,0.15)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = isActive ? '#ffffff26' : 'transparent';
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '34px',
          height: '34px',
          fontSize: '18px',
          borderRadius: '50%',
          backgroundColor: isActive ? '#ffffff33' : '#ffffff1a',
          marginRight: isExpanded ? '12px' : '0',
          transition: 'background-color 0.2s',
        }}>
          {icon}
        </span>
        {isExpanded && (
          <span style={{ fontSize: '15px', fontWeight: 600, letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {text}
            {isNew && (
              <span style={{
                backgroundColor: '#28a745',
                color: '#fff',
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '999px',
                fontWeight: 'bold',
                marginLeft: '4px',
              }}>NEW</span>
            )}
          </span>
        )}
      </Link>
    </li>
  );
};

// Back link from a dashboard to the NavigatorLaunch page. Rendered
// above /dashboard and /stablecoin when the stablecoin realm is active.
const BackToLaunch = ({ navigate, withRealm, label = "All dashboards" }) => (
  <button
    onClick={() => navigate(withRealm('/launch'))}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      background: 'transparent',
      border: 'none',
      color: '#0b6b76',
      fontSize: '13px',
      fontWeight: 600,
      padding: '0 0 12px 2px',
      cursor: 'pointer',
    }}
  >
    <FaChevronLeft style={{ fontSize: '11px' }} />
    {label}
  </button>
);

const NavItemExternalV1 = ({ url, icon, text, isExpanded, disabled = false }) => (
  <li style={{ marginBottom: '10px', width: '100%' }}>
    <a
      href={disabled ? undefined : url}
      target={disabled ? undefined : "_blank"}
      rel={disabled ? undefined : "noopener noreferrer"}
      title={!isExpanded ? text : disabled ? `${text} coming soon` : ""}
      onClick={(e) => { if (disabled) e.preventDefault(); }}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px',
        borderRadius: '10px',
        textDecoration: 'none',
        color: '#FFF',
        fontWeight: '500',
        background: 'transparent',
        borderLeft: '4px solid transparent',
        transition: 'all 0.2s ease-in-out',
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onMouseOver={(e) => {
        if (disabled) return;
        e.currentTarget.style.backgroundColor = '#ffffff20';
        e.currentTarget.style.transform = 'scale(1.01)';
        e.currentTarget.style.boxShadow = '0 0 8px rgba(0,0,0,0.15)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '34px',
        height: '34px',
        fontSize: '18px',
        borderRadius: '50%',
        backgroundColor: '#ffffff1a',
        marginRight: isExpanded ? '12px' : '0',
        transition: 'background-color 0.2s',
      }}>{icon}</span>
      {isExpanded && (
        <span style={{ fontSize: '15px', fontWeight: 600, letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {text}
          {disabled && (
            <span style={{
              backgroundColor: '#94A3B8',
              color: '#fff',
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '999px',
              fontWeight: 'bold',
              marginLeft: '4px',
            }}>ADD-ON</span>
          )}
        </span>
      )}
    </a>
  </li>
);

// ═══════════════════════════════════════════════════════════════════════════
// SidebarV2 — Idenza design-system sidebar.
// Layout (top-to-bottom):
//   [56px] workspace switcher — Idenza logo + tenant + chevron
//   [ 8px] gap
//   [32px] ⌘K command entry chip
//   [16px] gap
//   [—]    Rules group (with Approvals badge count)
//   [—]    Sandbox group
//   [—]    Dashboards group
//   [—]    External group
//   [—]    flex-grow spacer
//   [48px] footer — user avatar + name + · overflow menu (opens popover)
//
// Psychology moves applied:
//   Fitts's Law         — full-row 32px hit target
//   Serial Position     — Rules first (most-used), Reports last
//   Von Restorff        — the ONE `New` chip (indigo) among neutral chips
//   Zeigarnik effect    — Approvals badge count nags at the eye
//   Familiarity         — ⌘K chip, avatar bottom-left
//   Recognition         — Lucide icons + labels, keyboard hint on hover
// ═══════════════════════════════════════════════════════════════════════════
const s = {
  bg: 'var(--bg-surface)',
  border: 'var(--border-subtle)',
  hover: 'rgba(11,87,164,0.06)',
  active: 'rgba(11,87,164,0.10)',
  activeText: '#0B57A4',
  textPrimary: 'var(--text-primary)',
  textMuted: 'var(--text-muted)',
  textFaint: 'var(--text-tertiary)',
};

const SidebarV2 = ({ withRealm, realmInfo, showReportingDashboard, showStablecoinDashboard, showIsoDashboard, CASE_MGMT_BASE }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [footerOpen, setFooterOpen] = useState(false);
  const [flagsOpen, setFlagsOpen] = useState(false);
  const width = isExpanded ? 248 : 56;

  return (
    <aside
      style={{
        width,
        background: s.bg,
        borderRight: `1px solid ${s.border}`,
        transition: 'width 160ms cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        height: '100vh',
      }}
    >
      {/* Header — workspace switcher row */}
      <div
        style={{
          height: 56,
          padding: isExpanded ? '0 12px' : '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isExpanded ? 'space-between' : 'center',
          borderBottom: `1px solid ${s.border}`,
          flexShrink: 0,
        }}
      >
        {isExpanded ? (
          <>
            <WorkspaceButton realmInfo={realmInfo} />
            <IconGhostBtn onClick={() => setIsExpanded(false)} title="Collapse sidebar">
              <PanelLeftClose size={14} strokeWidth={1.75} />
            </IconGhostBtn>
          </>
        ) : (
          <IconGhostBtn onClick={() => setIsExpanded(true)} title="Expand sidebar">
            <PanelLeftOpen size={14} strokeWidth={1.75} />
          </IconGhostBtn>
        )}
      </div>

      {/* ⌘K entry — collapsed = search icon only */}
      {isExpanded && (
        <div style={{ padding: '10px 12px 6px' }}>
          <button
            type="button"
            onClick={() => { /* placeholder — wire to command palette when ready */ }}
            style={{
              width: '100%',
              height: 32,
              padding: '0 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              border: `1px solid ${s.border}`,
              borderRadius: 6,
              background: 'var(--bg-subtle)',
              color: s.textMuted,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'inherit',
              transition: 'background 120ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = s.hover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--bg-subtle)')}
          >
            <Search size={14} strokeWidth={1.75} />
            <span style={{ flex: 1, textAlign: 'left' }}>Search…</span>
            <Kbd>⌘K</Kbd>
          </button>
        </div>
      )}

      {/* Nav groups */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: isExpanded ? '6px 8px 12px' : '10px 4px' }}>
        <NavGroup label="Rules" isExpanded={isExpanded}>
          <NavItem to={withRealm("/rules")}            icon={<FileText size={16} strokeWidth={1.75} />}   text="Rules"            isExpanded={isExpanded} shortcut="G R" />
          <NavItem to={withRealm("/rulesets")}         icon={<Folders size={16} strokeWidth={1.75} />}    text="Rulesets"         isExpanded={isExpanded} shortcut="G S" />
          <NavItem to={withRealm("/approve-rulesets")} icon={<ShieldCheck size={16} strokeWidth={1.75} />} text="Approvals"       isExpanded={isExpanded} shortcut="G A" badge={3} />
        </NavGroup>

        <NavGroup label="Sandbox" isExpanded={isExpanded}>
          <NavItem to={withRealm("/sandbox")}           icon={<FlaskConical size={16} strokeWidth={1.75} />} text="Sandbox"        isExpanded={isExpanded} shortcut="G X" />
          <NavItem to={withRealm("/execution-results")} icon={<ListChecks size={16} strokeWidth={1.75} />}   text="Execution results" isExpanded={isExpanded} shortcut="G E" />
        </NavGroup>

        <NavGroup label="Dashboards" isExpanded={isExpanded}>
          {/* Senior's flow (07/28) — stablecoin realm routes through the
              NavigatorLaunch page at /launch. Non-stablecoin realms go
              straight to /dashboard. Item highlights on any of /launch,
              /dashboard, /stablecoin. */}
          <NavItem
            to={withRealm(showStablecoinDashboard ? "/launch" : "/dashboard")}
            icon={<LayoutDashboard size={16} strokeWidth={1.75} />}
            text={showStablecoinDashboard ? "Dashboards" : "Dashboard"}
            isExpanded={isExpanded}
            shortcut="G D"
            activeFor={["/launch", "/dashboard", "/stablecoin"]}
          />
          {showIsoDashboard && (
            <NavItem to={withRealm("/iso-portfolio")} icon={<LayoutDashboard size={16} strokeWidth={1.75} />} text="ISO portfolio" isExpanded={isExpanded} shortcut="G I" />
          )}
          {showReportingDashboard && (
            <NavItem to={withRealm("/reporting")} icon={<FileBarChart size={16} strokeWidth={1.75} />} text="Reporting" isExpanded={isExpanded} shortcut="G P" isNew />
          )}
        </NavGroup>

        {showCaseManagementLink && (
          <NavGroup label="External" isExpanded={isExpanded}>
            <NavItemExternal
              url={`${CASE_MGMT_BASE}/?realm=${encodeURIComponent(getCurrentRealm())}`}
              icon={<Briefcase size={16} strokeWidth={1.75} />}
              text="Case management"
              isExpanded={isExpanded}
              disabled={!enableCaseManagementLink}
            />
          </NavGroup>
        )}
      </nav>

      {/* Footer — avatar + name + overflow. Popover holds Logout. */}
      <div
        style={{
          borderTop: `1px solid ${s.border}`,
          padding: isExpanded ? '10px 12px' : '10px 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isExpanded ? 'space-between' : 'center',
          gap: 8,
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {isExpanded ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: s.active,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: s.activeText,
                  flexShrink: 0,
                  position: 'relative',
                }}
              >
                <CircleUser size={17} strokeWidth={1.5} />
                <span style={{
                  position: 'absolute',
                  right: -1,
                  bottom: -1,
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: '#22C55E',
                  border: `2px solid ${s.bg}`,
                }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span
                  title={realmInfo?.username || 'User'}
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: s.textPrimary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.2,
                  }}
                >
                  {realmInfo?.username || 'User'}
                </span>
                <span style={{ fontSize: 11, color: s.textMuted, lineHeight: 1.2 }}>
                  Sponsor bank ops
                </span>
              </div>
            </div>
            <IconGhostBtn onClick={() => setFooterOpen((v) => !v)} title="Account">
              <MoreHorizontal size={14} strokeWidth={1.75} />
            </IconGhostBtn>
          </>
        ) : (
          <IconGhostBtn onClick={() => setFooterOpen((v) => !v)} title="Account">
            <CircleUser size={17} strokeWidth={1.5} />
          </IconGhostBtn>
        )}

        {footerOpen && (
          <FooterPopover
            onClose={() => setFooterOpen(false)}
            onLogout={() => logout()}
            onFlags={() => { setFlagsOpen(true); setFooterOpen(false); }}
          />
        )}
      </div>

      {flagsOpen && <FeatureFlagsPanel onClose={() => setFlagsOpen(false)} />}
    </aside>
  );
};

// ── Workspace switcher (top of sidebar). Attio/Ramp pattern: logo +
// tenant name + chevron indicating this is a switchable context.
const WorkspaceButton = ({ realmInfo }) => (
  <button
    type="button"
    onClick={() => { /* placeholder — hook up tenant switcher when ready */ }}
    style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '4px 6px',
      background: 'transparent',
      border: 'none',
      borderRadius: 6,
      cursor: 'pointer',
      color: s.textPrimary,
      minWidth: 0,
      transition: 'background 120ms cubic-bezier(0.4, 0, 0.2, 1)',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = s.hover)}
    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    title={realmInfo?.tenantId ? `Tenant: ${realmInfo.tenantId}` : 'Workspace'}
  >
    <img
      src="/assets/idenza_logo.png"
      alt="Idenza"
      style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }}
    />
    <span
      style={{
        flex: 1,
        textAlign: 'left',
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: s.textPrimary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      Idenza
    </span>
    <ChevronDown size={12} strokeWidth={1.75} style={{ color: s.textMuted, flexShrink: 0 }} />
  </button>
);

// ── Section label + list.
const NavGroup = ({ label, isExpanded, children }) => (
  <div style={{ marginBottom: 14 }}>
    {isExpanded && (
      <div
        style={{
          padding: '10px 8px 6px',
          fontSize: 11,
          fontWeight: 500,
          color: s.textFaint,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
    )}
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {children}
    </ul>
  </div>
);

// ── Internal nav item.
//   badge    : number — muted count chip (Zeigarnik nag)
//   shortcut : "G R"  — keyboard hint, fades in on hover
//   isNew    : boolean — one indigo `New` chip max per sidebar (Von Restorff)
const NavItem = ({ to, icon, text, isExpanded, shortcut, badge, isNew = false, activeFor }) => {
  const currentPath = window.location.pathname;
  const isActive = activeFor
    ? activeFor.some((p) => currentPath.startsWith(p))
    : currentPath === (to.split("?")[0] || to);
  const [hover, setHover] = useState(false);
  const bg = isActive ? s.active : hover ? s.hover : 'transparent';
  const color = isActive ? s.activeText : s.textMuted;

  return (
    <li>
      <Link
        to={to}
        title={!isExpanded ? text : ''}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 32,
          padding: isExpanded ? '0 8px' : '0',
          justifyContent: isExpanded ? 'flex-start' : 'center',
          borderRadius: 6,
          textDecoration: 'none',
          color,
          fontSize: 13,
          fontWeight: isActive ? 500 : 400,
          background: bg,
          position: 'relative',
          transition: 'background 120ms cubic-bezier(0.4, 0, 0.2, 1)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {/* Left-edge indicator bar — the second cue for active state. */}
        {isActive && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              top: 6,
              bottom: 6,
              width: 2,
              borderRadius: 999,
              background: s.activeText,
            }}
          />
        )}
        <span style={{ display: 'inline-flex', color, flexShrink: 0 }}>
          {icon}
        </span>
        {isExpanded && (
          <>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
            {isNew && <Badge tone="new">New</Badge>}
            {badge !== undefined && badge !== null && <Badge tone="count">{badge}</Badge>}
            {hover && shortcut && !isActive && <Kbd>{shortcut}</Kbd>}
          </>
        )}
      </Link>
    </li>
  );
};

const NavItemExternal = ({ url, icon, text, isExpanded, disabled = false }) => {
  const [hover, setHover] = useState(false);
  const bg = hover && !disabled ? s.hover : 'transparent';
  const color = disabled ? s.textFaint : s.textMuted;
  return (
    <li>
      <a
        href={disabled ? undefined : url}
        target={disabled ? undefined : "_blank"}
        rel={disabled ? undefined : "noopener noreferrer"}
        title={!isExpanded ? text : disabled ? `${text} · coming soon` : ""}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={(e) => { if (disabled) e.preventDefault(); }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 32,
          padding: isExpanded ? '0 8px' : '0',
          justifyContent: isExpanded ? 'flex-start' : 'center',
          borderRadius: 6,
          textDecoration: 'none',
          color,
          fontSize: 13,
          fontWeight: 400,
          background: bg,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.75 : 1,
          transition: 'background 120ms cubic-bezier(0.4, 0, 0.2, 1)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <span style={{ display: 'inline-flex', color, flexShrink: 0 }}>{icon}</span>
        {isExpanded && (
          <>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
            {!disabled && <span style={{ fontSize: 11, color: s.textFaint }}>↗</span>}
            {disabled && <Badge tone="muted">Add-on</Badge>}
          </>
        )}
      </a>
    </li>
  );
};

// ── Badge — three tones:
//   count : muted numeric count (Zeigarnik)
//   new   : one indigo chip per sidebar (Von Restorff)
//   muted : outlined chip for external/add-on
const Badge = ({ children, tone = 'count' }) => {
  const styles = {
    count: {
      background: 'rgba(11,87,164,0.10)',
      color: '#0B57A4',
      fontWeight: 600,
    },
    new: {
      background: 'rgba(79,70,229,0.10)',
      color: '#4F46E5',
      fontWeight: 600,
    },
    muted: {
      background: 'transparent',
      color: s.textFaint,
      border: `1px solid ${s.border}`,
      fontWeight: 500,
    },
  };
  return (
    <span
      style={{
        fontSize: 10,
        padding: '1px 6px',
        borderRadius: 4,
        letterSpacing: 0,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        ...styles[tone],
      }}
    >
      {children}
    </span>
  );
};

// ── Kbd — keyboard shortcut chip that fades in on row hover.
const Kbd = ({ children }) => (
  <span
    style={{
      fontSize: 10,
      color: s.textFaint,
      fontFamily: `'Geist Mono', 'JetBrains Mono', 'Menlo', ui-monospace, monospace`,
      padding: '1px 5px',
      border: `1px solid ${s.border}`,
      borderRadius: 4,
      background: 'var(--bg-subtle)',
      fontWeight: 500,
    }}
  >
    {children}
  </span>
);

// ── Ghost icon button used for collapse toggle + workspace/footer actions.
const IconGhostBtn = ({ children, onClick, title }) => (
  <button
    onClick={onClick}
    title={title}
    aria-label={title}
    style={{
      width: 28,
      height: 28,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: 6,
      background: 'transparent',
      color: s.textMuted,
      cursor: 'pointer',
      padding: 0,
      transition: 'background 120ms cubic-bezier(0.4, 0, 0.2, 1)',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = s.hover)}
    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
  >
    {children}
  </button>
);

// ── Popover anchored to the footer overflow button. Holds account
// actions + a Feature flags entry. Click outside to dismiss.
const FooterPopover = ({ onClose, onLogout, onFlags }) => {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        left: 12,
        right: 12,
        bottom: 60,
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 8,
        boxShadow: '0 12px 24px -6px rgba(10, 10, 10, 0.12)',
        padding: 4,
        zIndex: 40,
      }}
    >
      <PopoverItem onClick={onFlags} icon={<ToggleRight size={14} strokeWidth={1.75} />}>Feature flags</PopoverItem>
      <div style={{ height: 1, background: s.border, margin: '4px 0' }} />
      <PopoverItem onClick={onLogout} icon={<LogOut size={14} strokeWidth={1.75} />}>Log out</PopoverItem>
    </div>
  );
};

const PopoverItem = ({ children, icon, onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        height: 32,
        padding: '0 8px',
        border: 'none',
        borderRadius: 6,
        background: hover ? s.hover : 'transparent',
        color: s.textPrimary,
        cursor: 'pointer',
        fontSize: 13,
        fontFamily: 'inherit',
        textAlign: 'left',
        transition: 'background 120ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <span style={{ display: 'inline-flex', color: s.textMuted, flexShrink: 0 }}>{icon}</span>
      <span>{children}</span>
    </button>
  );
};

export default NavigatorWithRoutes;
